"use client"

import { useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { createState, updateState, deleteState, getAllStates } from "../../slices/locationSlice"
import { toast } from "react-toastify"
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Divider,
} from "@mui/material"
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationCity as LocationCityIcon,
} from "@mui/icons-material"

const StatesTable = ({ states }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [stateName, setStateName] = useState("")
  const [currentState, setCurrentState] = useState(null)

  const handleAddState = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await dispatch(createState({ name: stateName })).unwrap()
      toast.success("State added successfully")
      setIsAddModalOpen(false)
      setStateName("")
      dispatch(getAllStates())
    } catch (error) {
      toast.error(error || "Failed to add state")
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditState = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await dispatch(updateState({ id: currentState._id, stateData: { name: stateName } })).unwrap()
      toast.success("State updated successfully")
      setIsEditModalOpen(false)
      setCurrentState(null)
      setStateName("")
      dispatch(getAllStates())
    } catch (error) {
      toast.error(error || "Failed to update state")
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteState = async () => {
    setIsLoading(true)

    try {
      await dispatch(deleteState(currentState._id)).unwrap()
      toast.success("State deleted successfully")
      setIsDeleteModalOpen(false)
      setCurrentState(null)
      // Explicitly fetch states again after deletion
      dispatch(getAllStates())
    } catch (error) {
      toast.error(error || "Failed to delete state")
    } finally {
      setIsLoading(false)
    }
  }

  const openEditModal = (state) => {
    setCurrentState(state)
    setStateName(state.name)
    setIsEditModalOpen(true)
  }

  const openDeleteModal = (state) => {
    setCurrentState(state)
    setIsDeleteModalOpen(true)
  }

  const navigateToCities = (stateId) => {
    navigate(`/locations/${stateId}/cities`)
  }

  return (
    <Card elevation={2}>
      <CardHeader
        title="States"
        subheader="Manage states and their cities"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setStateName("")
              setIsAddModalOpen(true)
            }}
          >
            Add State
          </Button>
        }
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell width={80}>SR. NO.</TableCell>
                <TableCell>NAME</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {states.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No states found. Add your first state!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                states.map((state, index) => (
                  <TableRow key={state._id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{state.name}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LocationCityIcon />}
                          onClick={() => navigateToCities(state._id)}
                        >
                          Cities ({state.cityCount || 0})
                        </Button>
                        <Tooltip title="Edit">
                          <IconButton color="primary" onClick={() => openEditModal(state)} size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => openDeleteModal(state)} size="small">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {/* Add State Dialog */}
      <Dialog open={isAddModalOpen} onClose={() => !isLoading && setIsAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add State</DialogTitle>
        <form onSubmit={handleAddState}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter the name of the new state to add it to your system.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="stateName"
              label="State Name"
              type="text"
              fullWidth
              variant="outlined"
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              required
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setIsAddModalOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !stateName.trim()}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? "Adding..." : "Add State"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit State Dialog */}
      <Dialog open={isEditModalOpen} onClose={() => !isLoading && setIsEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit State</DialogTitle>
        <form onSubmit={handleEditState}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>Update the name of the selected state.</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="editStateName"
              label="State Name"
              type="text"
              fullWidth
              variant="outlined"
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              required
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setIsEditModalOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || !stateName.trim()}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? "Updating..." : "Update State"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete State Dialog */}
      <Dialog open={isDeleteModalOpen} onClose={() => !isLoading && setIsDeleteModalOpen(false)} maxWidth="sm">
        <DialogTitle>Delete State</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{currentState?.name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: "error.main" }}>
            This will also delete all cities associated with this state. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDeleteModalOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteState}
            color="error"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

// const getAllStates = () => async (dispatch) => {
//   // Placeholder for getAllStates action
//   // Replace this with your actual implementation
//   console.log("getAllStates action dispatched");
// };

export default StatesTable

